/**
 * Fall notifications and the screen shake that sells them.
 *
 * Three registers, because a 9 m slip and a 90 m plummet are not the same event:
 *
 * - `minor`    — a grey line that slides in and leaves. `Fell 12 m`.
 * - `progress` — the one that stings: `You lost 47 m of progress.`
 * - `major`    — a dark-red flash across the middle of the screen for the falls
 *                that end runs. One at a time; a second replaces the first.
 *
 * Copy is the caller's job. Keep it dry — the joke is the fall, not the caption.
 */

import './ui.css';

export type ToastKind = 'minor' | 'major' | 'progress';

/** How long a toast lives before it starts fading. */
const LIFETIME_MS = 2500;
const FADE_MS = 240;
/** More than this on screen and it reads as spam, not information. */
const MAX_STACK = 2;
const SHAKE_MS = 250;

interface LiveToast {
  el: HTMLElement;
  timer: ReturnType<typeof setTimeout>;
}

let stackEl: HTMLElement | null = null;
let flashEl: HTMLElement | null = null;
const stack: LiveToast[] = [];
let flash: LiveToast | null = null;
let shakeTimer: ReturnType<typeof setTimeout> | null = null;

function root(): HTMLElement {
  return document.getElementById('ui-root') ?? document.body;
}

/** True when the player has asked the OS to stop moving things about. */
function reducedMotion(): boolean {
  return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

function stackContainer(): HTMLElement {
  if (stackEl === null) {
    stackEl = document.createElement('div');
    stackEl.className = 'sk-toasts';
    root().appendChild(stackEl);
  }
  return stackEl;
}

function flashContainer(): HTMLElement {
  if (flashEl === null) {
    flashEl = document.createElement('div');
    flashEl.className = 'sk-flash';
    root().appendChild(flashEl);
  }
  return flashEl;
}

function retire(t: LiveToast, remove: boolean): void {
  clearTimeout(t.timer);
  t.el.classList.remove('is-in');
  if (remove) setTimeout(() => t.el.remove(), FADE_MS);
}

/**
 * Show a message.
 *
 * Never throws and never blocks — this is called from the physics step's fall
 * handler, and a UI hiccup must not cost a frame.
 */
export function toast(msg: string, kind: ToastKind = 'minor'): void {
  if (kind === 'major') {
    const el = flashContainer();
    if (flash !== null) clearTimeout(flash.timer);
    el.textContent = msg;
    el.classList.remove('is-in');
    // Force a reflow so a replacement flash re-runs its transition from scratch.
    void el.offsetWidth;
    el.classList.add('is-in');
    flash = {
      el,
      timer: setTimeout(() => {
        el.classList.remove('is-in');
        flash = null;
      }, LIFETIME_MS),
    };
    return;
  }

  const container = stackContainer();
  const el = document.createElement('div');
  el.className = kind === 'progress' ? 'sk-toast sk-toast--progress' : 'sk-toast';
  el.textContent = msg;
  container.appendChild(el);

  const live: LiveToast = {
    el,
    timer: setTimeout(() => {
      el.classList.remove('is-in');
      setTimeout(() => el.remove(), FADE_MS);
      const i = stack.indexOf(live);
      if (i >= 0) stack.splice(i, 1);
    }, LIFETIME_MS),
  };
  stack.push(live);

  // Oldest first out, so the newest message is always readable.
  while (stack.length > MAX_STACK) {
    const dropped = stack.shift();
    if (dropped !== undefined) retire(dropped, true);
  }

  // Next frame, so the transition has an initial state to move away from.
  requestAnimationFrame(() => el.classList.add('is-in'));
}

/**
 * Rattle the screen for a quarter second. Used with a `major` toast on a big
 * fall — the shake is the punchline and the toast is the caption.
 *
 * Respects `prefers-reduced-motion`: for some players this is not a flourish,
 * it is a symptom.
 */
export function screenShake(el?: HTMLElement): void {
  if (reducedMotion()) return;
  const target = el ?? document.body;
  if (shakeTimer !== null) clearTimeout(shakeTimer);
  target.classList.remove('sk-shake');
  void target.offsetWidth; // restart the animation even if one is running
  target.classList.add('sk-shake');
  shakeTimer = setTimeout(() => {
    target.classList.remove('sk-shake');
    shakeTimer = null;
  }, SHAKE_MS);
}

/** Drop everything on screen at once. Used when a run restarts. */
export function clearToasts(): void {
  for (const t of stack) retire(t, true);
  stack.length = 0;
  if (flash !== null) {
    retire(flash, false);
    flash = null;
  }
}
